import format from 'string-template';

import { pathPlaceholder, storagePaths } from './firebasePaths';

/**
 * Long-format workout sessions (S2-9075).
 *
 * A long session is a workout containing exactly one activity, of type LONG_SESSION, backed by one
 * video the client plays straight through. This module holds the vocabulary shared by the builder,
 * the upload provider and the workouts list. It mirrors `functions-22/src/longSession/` — the string
 * values are part of the Firestore contract and must match on both sides.
 */

/** How a workout is authored and played. Absent on every pre-existing workout, which means STANDARD. */
const WorkoutSessionType = {
  STANDARD: 'STANDARD',
  LONG_SESSION: 'LONG_SESSION',
};

/**
 * Whether a workout may be assigned. Absent means READY, so no existing workout needs a backfill.
 * Named to avoid colliding with the *assignment* status, which is a different thing entirely.
 */
const WorkoutPublishStatus = {
  PENDING: 'PENDING',
  READY: 'READY',
  FAILED: 'FAILED',
};

/** Where a long session's video has got to. Only the server writes anything past UPLOADING. */
const LongSessionStage = {
  UPLOADING: 'UPLOADING',
  UPLOADED: 'UPLOADED',
  OPTIMIZING: 'OPTIMIZING',
  OPTIMIZED: 'OPTIMIZED',
  TRANSCODING: 'TRANSCODING',
};

/**
 * Why a session stopped before its video was in. Recorded under `videoProcessing.error` rather than
 * as a publish status: both outcomes leave a session waiting for a video, which is what every
 * consumer of `publishStatus` already knows how to handle.
 */
const LongSessionFailureCause = {
  CANCELLED: 'CANCELLED',
  UPLOAD_ERROR: 'UPLOAD_ERROR',
};

/** Stands in for a file that arrives with no usable name of its own. */
const FALLBACK_SOURCE_FILE_NAME = 'source';

/**
 * Cloud Storage caps an object name at 1024 bytes; this leaves ample room for the prefix, the coach
 * id and the workout id while still accepting any filename a coach would realistically use.
 */
const MAX_SOURCE_FILE_NAME_LENGTH = 200;

/**
 * Upload ceiling. Checked before `put()` so an over-size file gets a real message rather than
 * the `storage/unauthorized` the rule would return mid-upload.
 *
 * Keep in sync with the literal in `storage.rules`, which is what actually enforces it.
 */
const MAX_SOURCE_BYTES = 4 * 1024 * 1024 * 1024;

/**
 * The name to store the coach's video under — their own file name, kept as-is.
 *
 * Keeping it means the extension travels with the file for free, so the object opens from the
 * Storage console and from any signed URL. Only two things are imposed: no path separators, since
 * the object must stay a single segment inside the workout's folder, and a length cap. The tail is
 * kept when truncating, because that is where the extension lives.
 *
 * @param {string} [fileName]
 * @returns {string}
 */
const getSourceFileName = (fileName = '') => {
  const cleaned = fileName.replace(/[/\\]+/g, '-').trim();
  if (!cleaned) {
    return FALLBACK_SOURCE_FILE_NAME;
  }
  return cleaned.slice(-MAX_SOURCE_FILE_NAME_LENGTH);
};

/**
 * Builds the Storage path a long session's source video lives at.
 *
 * The path is recorded on the workout before the first byte is sent, so everything that needs it
 * later — cancel, discard, the server's own validation — reads it from the document rather than
 * rebuilding it from a convention.
 *
 * @param {string} coachId The coach who owns the workout — not necessarily the uploader.
 * @param {string} workoutId
 * @param {string} [fileName] The coach's file name.
 * @returns {string}
 */
const getLongSessionSourcePath = (coachId, workoutId, fileName) => {
  const folder = format(storagePaths.LONG_SESSION_UPLOADS, {
    [pathPlaceholder.COACH_ID]: coachId,
    [pathPlaceholder.WORKOUT_ID]: workoutId,
  });
  return `${folder}/${getSourceFileName(fileName)}`;
};

/**
 * Builds the Storage path a product's welcome video is uploaded to, recorded on the product's
 * `welcomeVideoUpload` before the first byte is sent, as a long session records its own.
 *
 * @param {string} coachId The coach who owns the product — not necessarily the uploader.
 * @param {string} productId
 * @param {string} [fileName] The coach's file name.
 * @returns {string}
 */
const getWelcomeVideoSourcePath = (coachId, productId, fileName) => {
  const folder = format(storagePaths.WELCOME_VIDEO_UPLOADS, {
    [pathPlaceholder.COACH_ID]: coachId,
    [pathPlaceholder.PRODUCT_ID]: productId,
  });
  return `${folder}/${getSourceFileName(fileName)}`;
};

/**
 * Whether a workout is a long-format session.
 *
 * Reads `sessionType` only, with no fallback to the activity shape. The save boundary guarantees the
 * two agree and every legacy workout is STANDARD, so a fallback could never fire — omitting it
 * removes a class of "which one wins" bugs.
 *
 * @param {Object} workoutData Raw workout data (or `workoutContent` from an assignment).
 * @returns {boolean}
 */
const isLongSessionWorkout = (workoutData) => (
  workoutData?.sessionType === WorkoutSessionType.LONG_SESSION
);

/**
 * Whether a video's bytes died with a closed tab: its document still waits at UPLOADING and this
 * browser session holds no transfer for it, so only sending the file again moves it on.
 *
 * @param {Object} status
 * @param {string} [status.publishStatus] The document's publish status.
 * @param {string} [status.stage] The document's `videoProcessing.stage`.
 * @param {Object} [status.upload] This tab's transfer entry for the document, if any.
 * @returns {boolean}
 */
const isUploadInterrupted = ({ publishStatus, stage, upload }) => (
  publishStatus === WorkoutPublishStatus.PENDING && stage === LongSessionStage.UPLOADING && !upload
);

/**
 * Whether a workout is READY for clients. Absent `publishStatus` means READY, so every workout that
 * predates long sessions passes. The same rule as `isWorkoutAssignable` on the server and mobile.
 *
 * @param {Object} workoutData Raw workout data, or a Workout document.
 * @returns {boolean}
 */
const isWorkoutPublished = (workoutData) => (
  !workoutData?.publishStatus || workoutData.publishStatus === WorkoutPublishStatus.READY
);

export {
  WorkoutSessionType,
  WorkoutPublishStatus,
  LongSessionStage,
  LongSessionFailureCause,
  MAX_SOURCE_BYTES,
  getSourceFileName,
  getLongSessionSourcePath,
  getWelcomeVideoSourcePath,
  isLongSessionWorkout,
  isUploadInterrupted,
  isWorkoutPublished,
};
